/**
 * Turning a run of SNMP poll results into charts.
 *
 * The port of `MonitorChartBuilder`. It uses the same `ChartSpec` shape and the same palette as the
 * device's own live charts, deliberately: for a machine that is in the fleet twice the two cards sit
 * on the same page, and a second colour scheme would read as a second kind of data.
 *
 * **What becomes a chart is a rule, not a list.** A reading is plotted only if the agent gave it a
 * number - "Normal" and "On battery" are labels with no height - and readings are then grouped into
 * charts by **(group, unit, scale)**. Three disk temperatures are one chart with three lines and a
 * legend; CPU and network throughput are separate charts, because nothing about their axes is
 * comparable. That rule is what lets the agent's collectors add a metric without anything here
 * changing.
 */
import type { MetricsReport } from './contracts.gen.js';
import type { ChartSpec } from './ui/ChartCard.js';
import type { Series } from './ui/Sparkline.js';

/** One plotted line: a metric's readings over the window, with its latest display text. */
interface SeriesData {
  name: string;
  group: string;
  unit: string;
  max: number | null;
  latestText: string;
  readonly points: number[];
  readonly at: number[];
}

export function buildMonitorCharts(
  source: readonly MetricsReport[],
  windowMs: number
): ChartSpec[] {
  if (source.length === 0) return [];

  // The window ends at the newest sample's own timestamp - the polling AGENT's clock, not the
  // browser's, so skew between them cannot slide the graph.
  const sorted = [...source].sort((a, b) => Date.parse(a.polledUtc) - Date.parse(b.polledUtc));
  const end = Date.parse(sorted[sorted.length - 1]!.polledUtc);
  const start = end - windowMs;
  const samples = sorted.filter(s => Date.parse(s.polledUtc) >= start);
  if (samples.length === 0) return [];

  // Pivot: samples are lists of readings, charts are readings over time. Insertion-ordered, so the
  // charts appear in the order the collector emitted them - which is the order a person would put
  // them in (CPU before toner).
  const series: SeriesData[] = [];
  const byKey = new Map<string, SeriesData>();

  for (const sample of samples) {
    const at = Date.parse(sample.polledUtc);
    for (const metric of sample.metrics ?? []) {
      if (!metric.isChartable || metric.numeric === null) continue;
      let data = byKey.get(metric.seriesKey);
      if (!data) {
        data = {
          name: metric.name,
          group: metric.group || 'General',
          unit: metric.unit ?? '',
          max: metric.max,
          latestText: '',
          points: [],
          at: []
        };
        byKey.set(metric.seriesKey, data);
        series.push(data);
      }
      data.points.push(metric.numeric);
      data.at.push(at);
      data.latestText = metric.value;
    }
  }

  if (series.length === 0) return [];

  // Readings that share a group, a unit and a scale belong on one pair of axes.
  const families = new Map<string, SeriesData[]>();
  for (const s of series) {
    const key = `${s.group} ${s.unit} ${s.max ?? 'auto'}`;
    const family = families.get(key);
    if (family) family.push(s);
    else families.set(key, [s]);
  }

  const charts: ChartSpec[] = [];
  const titles = new Set<string>();

  for (const members of families.values()) {
    const first = members[0]!;
    // Every member of a family shares a scale by construction; when it is autoscaled the ceiling is
    // the family's own peak across the window, so both directions of a throughput chart stay
    // comparable to each other.
    const scale =
      first.max ?? Math.max(0, ...members.flatMap(m => m.points));

    const chartSeries: Series[] = members.map((m, i) => ({
      label: m.name,
      colorIndex: i,
      points: m.points,
      at: m.at
    }));

    charts.push({
      title: disambiguate(members.length === 1 ? first.name : first.group, first.unit, titles),
      // A single reading names its own current value; a family's currents belong in the legend
      // beside each line, so the header carries the shared ceiling instead.
      current:
        members.length === 1
          ? first.latestText
          : first.max === null
            ? `peak ${formatCeiling(scale, first.unit)}`
            : '',
      max: scale > 0 ? scale : null,
      format: v => formatCeiling(v, first.unit),
      series: chartSeries,
      ...(members.length > 1
        ? {
            legend: members.map((m, i) => ({
              text: `${m.name} ${m.latestText}`.trim(),
              colorIndex: i
            }))
          }
        : {})
    });
  }

  return charts;
}

/**
 * Why there is nothing to draw.
 *
 * **Three genuinely different states, and they used to share one sentence** - the one they shared,
 * "waiting for the first poll", being the wrong answer for two of them. A device polled every few
 * seconds read as one the CRM had never reached, so a technician went looking at the polling agent
 * instead of at the device. What matters is whether samples are arriving, and if they are, whether
 * anything in them is a measurement rather than a label.
 */
export function monitorWaitingText(
  source: readonly MetricsReport[],
  maintenanceView: boolean
): string {
  if (source.length === 0) {
    return maintenanceView
      ? 'No maintenance samples have been collected yet.'
      : 'Waiting for the first poll…';
  }
  if (!source[source.length - 1]!.reachable) {
    return "The device didn't answer the last poll, so there is nothing to graph yet.";
  }
  // Polls are landing and every reading in them is a label (a model name, "Normal", a firmware
  // version). Naming what to check matters: the usual cause is the monitor's device type being
  // wrong, which selects the wrong metric set entirely.
  return (
    'This device is being polled, but nothing it reports is a measurement that can be graphed — ' +
    'every reading is a label. Check that the device type on this monitor is right, and that SNMP is ' +
    "exposing the device's performance counters."
  );
}

/**
 * Keeps two charts from carrying the same heading.
 *
 * It happens when one group yields more than one family - a switch reporting both "Interfaces up"
 * and throughput under Network - and two identical headings above different axes is worse than a
 * slightly clumsy one.
 */
function disambiguate(title: string, unit: string, used: Set<string>): string {
  let candidate = title;
  if (used.has(candidate) && unit) candidate = `${title} · ${unit}`;
  let n = 2;
  while (used.has(candidate)) candidate = `${title} (${n++})`;
  used.add(candidate);
  return candidate;
}

/**
 * Formats an autoscaled ceiling.
 *
 * Bytes per second is the one unit worth scaling by hand: "1.2 MB/s" against a raw "1258291" is the
 * difference between a readable header and a number nobody parses.
 */
function formatCeiling(value: number, unit: string): string {
  if (unit === 'B/s') {
    const KB = 1024;
    const MB = KB * 1024;
    if (value >= MB) return `${round1(value / MB)} MB/s`;
    return `${round1(value / KB)} KB/s`;
  }
  return unit ? `${round1(value)} ${unit}` : round1(value);
}

function round1(value: number): string {
  return (Math.round(value * 10) / 10).toString();
}
